import type { CasesAnalyticsSummary } from '@compass/client/types/cases_analytics';
import { AlertTriangle, CheckCircle2, ClipboardCheck } from 'lucide-react';
import React from 'react';

import { KpiCard } from '@/pages/analytics/components/KpiCard';
import {
  formatCountDelta,
  formatInt,
  formatPpDelta,
  formatRatePercent,
} from '@/pages/analytics/utils/formatters';

interface CasesKpiGridProps {
  summary?: CasesAnalyticsSummary;
  loading?: boolean;
  /** Scrolls to the SLA breached cases table; the link shows only when breached > 0. */
  onViewBreachedCases?: () => void;
}

const rate = (part: number, total: number): number => (total > 0 ? part / total : 0);

export const CasesKpiGrid: React.FC<CasesKpiGridProps> = ({
  summary,
  loading,
  onViewBreachedCases,
}) => {
  const submitted = summary?.submitted_cases?.value ?? 0;
  const within = summary?.within_sla_cases?.value ?? 0;
  const breached = summary?.breached_cases?.value ?? 0;
  const priorSubmitted = summary?.submitted_cases?.prior_value;
  const priorWithin = summary?.within_sla_cases?.prior_value;
  const priorBreached = summary?.breached_cases?.prior_value;
  // A prior rate needs both of its prior counts; otherwise there is nothing to compare with.
  const priorRate = (part: number | undefined): number | undefined =>
    part == null || priorSubmitted == null ? undefined : rate(part, priorSubmitted);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <KpiCard
        icon={ClipboardCheck}
        iconClassName="bg-primary/10 text-primary"
        label="All Submitted"
        value={formatInt(submitted)}
        delta={formatCountDelta(submitted, priorSubmitted, 'up-is-good')}
        loading={loading}
      />
      <KpiCard
        icon={CheckCircle2}
        iconClassName="bg-positive text-positive-foreground"
        label="Within SLA"
        value={formatInt(within)}
        secondaryValue={formatRatePercent(rate(within, submitted))}
        delta={formatPpDelta(rate(within, submitted), priorRate(priorWithin), 'up-is-good')}
        loading={loading}
      />
      <KpiCard
        icon={AlertTriangle}
        iconClassName="bg-attention text-attention-foreground"
        label="SLA Breached"
        value={formatInt(breached)}
        secondaryValue={formatRatePercent(rate(breached, submitted))}
        delta={formatPpDelta(rate(breached, submitted), priorRate(priorBreached), 'down-is-good')}
        action={
          !loading && breached > 0 && onViewBreachedCases ? (
            <button
              type="button"
              onClick={onViewBreachedCases}
              className="text-xs font-medium text-primary hover:underline"
            >
              View cases →
            </button>
          ) : undefined
        }
        loading={loading}
      />
    </div>
  );
};
